import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Users, DollarSign, Activity, Bell, FileText, Settings as SettingsIcon, HelpCircle, User, Mail, Phone, MapPin, Shield, ShoppingBag, CreditCard } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import toast from 'react-hot-toast';

const Dashboard = () => {
    const { user } = useAuth();
    const [stats, setStats] = useState([]);
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                const [statsRes, activityRes] = await Promise.all([
                    api.get('/dashboard/stats'),
                    api.get('/dashboard/activity')
                ]);

                // Map icons back to stats since they can't be sent over JSON
                const iconMap = {
                    orders: ShoppingBag,
                    revenue: CreditCard,
                    pending: Bell,
                    active: Shield
                };

                const mappedStats = statsRes.data.data.map(stat => ({
                    ...stat,
                    icon: iconMap[stat.type] || Activity
                }));

                setStats(mappedStats);
                setActivities(activityRes.data.data);
            } catch (error) {
                console.error('Error fetching dashboard data:', error);
                toast.error('Could not fetch real-time dashboard data');
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, []);

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Page Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-black text-slate-900 dark:text-white">Dashboard</h1>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Hello, {user?.name || 'User'}! Welcome back.</p>
                </div>
                <div className="bg-white dark:bg-slate-900/50 p-1.5 rounded-2xl border border-slate-200 dark:border-white/10 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary font-bold">
                        {user?.role === 'admin' ? <Shield size={20} /> : <User size={20} />}
                    </div>
                    <div className="pr-4">
                        <p className="text-[10px] font-black uppercase tracking-widest text-primary">Current Role</p>
                        <p className="text-sm font-bold text-slate-900 dark:text-white capitalize">{user?.role || 'Member'}</p>
                    </div>
                </div>
            </div>

            {/* Quick Profile Overview */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gradient-to-br from-primary/5 via-transparent to-purple-600/5 rounded-[1.5rem] md:rounded-[2.5rem] border border-primary/20 p-5 md:p-8 relative overflow-hidden group"
            >
                <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2 group-hover:bg-primary/10 transition-colors"></div>

                <div className="relative z-10 flex flex-col lg:flex-row items-center gap-8">
                    {/* Avatar */}
                    <div className="relative">
                        <div className="w-16 h-16 md:w-24 md:h-24 rounded-2xl md:rounded-[2rem] bg-gradient-to-br from-primary to-purple-600 p-0.5 md:p-1">
                            <div className="w-full h-full bg-white dark:bg-slate-900 rounded-[0.9rem] md:rounded-[1.8rem] flex items-center justify-center text-2xl md:text-3xl font-black text-primary">
                                {user?.name?.charAt(0).toUpperCase()}
                            </div>
                        </div>
                        <div className="absolute -bottom-1 -right-1 bg-green-500 w-4 h-4 md:w-5 md:h-5 rounded-full border-2 md:border-4 border-white dark:border-slate-900"></div>
                    </div>

                    {/* Basic Info */}
                    <div className="flex-1 text-center lg:text-left space-y-2 md:space-y-4">
                        <div>
                            <h2 className="text-xl md:text-2xl font-black text-slate-900 dark:text-white ">{user?.name}</h2>
                            <p className="text-xs md:text-sm text-slate-500 dark:text-slate-400 font-medium">{user?.email}</p>
                        </div>

                        <div className="flex flex-wrap justify-center lg:justify-start gap-2 md:gap-4">
                            <div className="flex items-center gap-1.5 bg-white dark:bg-white/5 py-1.5 px-3 rounded-lg border border-slate-200 dark:border-white/10">
                                <Mail size={12} className="text-primary" />
                                <span className="text-[10px] font-bold dark:text-white uppercase tracking-wider">{user?.email}</span>
                            </div>
                            {user?.phone && (
                                <div className="flex items-center gap-1.5 bg-white dark:bg-white/5 py-1.5 px-3 rounded-lg border border-slate-200 dark:border-white/10">
                                    <Phone size={12} className="text-primary" />
                                    <span className="text-[10px] font-bold dark:text-white">{user?.phone}</span>
                                </div>
                            )}
                            <div className="flex items-center gap-1.5 bg-white dark:bg-white/5 py-1.5 px-3 rounded-lg border border-slate-200 dark:border-white/10">
                                <Activity size={12} className="text-primary" />
                                <span className="text-[10px] font-bold dark:text-white uppercase tracking-wider">Active</span>
                            </div>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 lg:flex-row shrink-0">
                        <button className="px-4 py-2 bg-primary text-white font-black rounded-lg shadow-xl shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] transition-all text-[10px] uppercase tracking-widest">
                            Edit Profile
                        </button>
                    </div>
                </div>
            </motion.div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {stats.map((stat, index) => (
                    <motion.div
                        key={stat.label}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="bg-white dark:bg-slate-900/50 p-6 rounded-2xl border border-slate-200 dark:border-white/10 hover:shadow-xl transition-all"
                    >
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="text-xs text-slate-500 dark:text-slate-400 font-semibold">{stat.label}</p>
                                <h3 className="text-2xl font-black text-slate-900 dark:text-white mt-1">{stat.value}</h3>
                                <p className="text-[10px] text-green-600 font-semibold mt-1">{stat.change} last month</p>
                            </div>
                            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center`}>
                                <stat.icon size={24} className="text-white" />
                            </div>
                        </div>
                    </motion.div>
                ))}
            </div>

            {/* Recent Activity & Quick Actions */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Activity Card */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="bg-white dark:bg-slate-900/50 p-6 rounded-2xl border border-slate-200 dark:border-white/10"
                >
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-xl font-black text-slate-900 dark:text-white">Recent Activity</h2>
                        <button
                            onClick={() => navigate('/dashboard/orders')}
                            className="text-primary text-sm font-bold hover:underline"
                        >
                            View All
                        </button>
                    </div>
                    <div className="space-y-4">
                        {activities.length > 0 ? activities.map((activity) => (
                            <div key={activity.id} className="flex items-center gap-4 p-4 hover:bg-slate-50 dark:hover:bg-white/5 rounded-2xl transition-all group">
                                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary group-hover:scale-110 transition-transform">
                                    {activity.type === 'payment_success' ? <CreditCard size={20} /> :
                                        activity.type === 'order_status' ? <ShoppingBag size={20} /> :
                                            <Bell size={20} />}
                                </div>
                                <div className="flex-1">
                                    <p className="font-bold text-slate-900 dark:text-white">{activity.message}</p>
                                    <p className="text-sm text-slate-500 dark:text-slate-400">{activity.time}</p>
                                </div>
                            </div>
                        )) : (
                            <div className="text-center py-10">
                                <p className="text-slate-500 dark:text-slate-400 text-sm italic">No recent activity found.</p>
                            </div>
                        )}
                    </div>
                </motion.div>

                {/* Quick Actions */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="bg-white dark:bg-slate-900/50 p-6 rounded-2xl border border-slate-200 dark:border-white/10"
                >
                    <h2 className="text-xl font-black text-slate-900 dark:text-white mb-6">Quick Actions</h2>
                    <div className="grid grid-cols-2 gap-4">
                        {[
                            { label: 'Shop Now', icon: ShoppingBag, color: 'text-blue-500 bg-blue-500/10', path: '/products' },
                            { label: 'My Orders', icon: FileText, color: 'text-green-500 bg-green-500/10', path: '/dashboard/orders' },
                            { label: 'My Profile', icon: User, color: 'text-purple-500 bg-purple-500/10', path: '/dashboard/profile' },
                            { label: 'Get Support', icon: HelpCircle, color: 'text-orange-500 bg-orange-500/10', path: '/contact' },
                        ].map((action) => (
                            <button
                                key={action.label}
                                onClick={() => navigate(action.path)}
                                className="flex flex-col items-center gap-3 p-6 bg-slate-50 dark:bg-white/5 hover:bg-primary/5 dark:hover:bg-primary/20 rounded-2xl transition-all hover:scale-105 border border-transparent hover:border-primary/20"
                            >
                                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${action.color}`}>
                                    <action.icon size={24} />
                                </div>
                                <span className="font-bold text-slate-900 dark:text-white text-sm">{action.label}</span>
                            </button>
                        ))}
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default Dashboard;
